export const TACTICAL_DESCRIPTIONS: Record<string, { shortExplanation: string; description: string; neutralizer: string }> = {
  GASLIGHTING: {
    shortExplanation: "Denies your version of events to make you doubt your own memory.",
    description: "Rewrites shared reality so the other person controls which version of events is 'true.' This goes beyond disagreement — it targets your ability to trust your own perception.",
    neutralizer: "Grey Rock Response Pattern",
  },
  INVALIDATION: {
    shortExplanation: "Dismisses the emotional reaction by framing sensitivity as the problem.",
    description: "Shifts focus from the issue to the other person's feelings, treating the emotional response as a character flaw rather than a valid reaction to what happened.",
    neutralizer: "Emotional Boundary Reinforcement",
  },
  MINIMIZATION: {
    shortExplanation: "Downplays the impact to avoid taking responsibility.",
    description: "Reduces the significance of harmful behavior so the other person's reaction seems disproportionate. The message becomes: it wasn't that bad, so you shouldn't feel that way.",
    neutralizer: "Reality Anchoring Protocol",
  },
  "GUILT-TRIPPING": {
    shortExplanation: "Uses sacrifice and obligation to pressure compliance.",
    description: "Frames past effort as emotional debt, implying the other person owes a certain response because of what the speaker claims they've done or given up.",
    neutralizer: "Obligation Boundary Protocol",
  },
  TRIANGULATION: {
    shortExplanation: "Invokes outside opinions to isolate your position.",
    description: "Brings in third-party agreement — real or manufactured — to make the other person feel outnumbered. The goal is to make disagreement feel like denial rather than a valid stance.",
    neutralizer: "Direct Communication Protocol",
  },
  DEFLECTION: {
    shortExplanation: "Redirects the conversation to avoid accountability.",
    description: "Changes the subject or raises unrelated issues whenever the real topic gets uncomfortable. The original concern never gets addressed because the focus keeps shifting.",
    neutralizer: "Topic Anchoring Protocol",
  },
  "BLAME SHIFTING": {
    shortExplanation: "Reverses responsibility so you end up defending yourself.",
    description: "Flips the dynamic so the person raising the concern becomes the one being questioned. The original issue disappears because you're now explaining your own behavior instead.",
    neutralizer: "Responsibility Clarification Protocol",
  },
  "PASSIVE AGGRESSION": {
    shortExplanation: "Expresses hostility indirectly while maintaining deniability.",
    description: "Conveys disapproval through tone, timing, or implication rather than direct words. If confronted, the speaker can claim innocence — 'I didn't say anything wrong.'",
    neutralizer: "Direct Address Protocol",
  },
  "CONTROL FRAMING": {
    shortExplanation: "Positions their view as the only reasonable option.",
    description: "Asserts authority over the other person's choices by framing alternatives as irrational or irresponsible. Disagreement becomes defiance rather than a difference of opinion.",
    neutralizer: "Autonomy Reinforcement Protocol",
  },
  "VICTIM REVERSAL": {
    shortExplanation: "Repositions the aggressor as the victim to deflect accountability.",
    description: "Flips the narrative so the person who caused harm becomes the one who is suffering. This forces the other person to comfort or reassure instead of addressing the original issue.",
    neutralizer: "Reality Anchoring Protocol",
  },
  "FALSE CONSENSUS": {
    shortExplanation: "Claims everyone agrees to pressure you into backing down.",
    description: "Manufactures broad agreement without naming specific people. The vagueness is the point — it creates social pressure that's impossible to verify or challenge directly.",
    neutralizer: "Individual Position Protocol",
  },
  "MORAL SUPERIORITY": {
    shortExplanation: "Claims the ethical high ground to shame you into submission.",
    description: "Positions the speaker as morally above the conversation, making the other person feel wrong for even raising the issue. Engagement becomes a moral failing.",
    neutralizer: "Value Neutrality Protocol",
  },
  SHAMING: {
    shortExplanation: "Attacks identity or character to erode self-worth.",
    description: "Goes beyond criticizing behavior — it targets who the other person is. The goal is to make them feel fundamentally flawed, which silences pushback from the inside out.",
    neutralizer: "Identity Boundary Protocol",
  },
  "TONE POLICING": {
    shortExplanation: "Criticizes how you say something to avoid what you're saying.",
    description: "Makes the delivery the issue instead of the content. If you're too emotional, too calm, too direct, or too indirect — the focus shifts from the problem to your communication style.",
    neutralizer: "Content Redirect Protocol",
  },
  "FUTURE PROPOSING": {
    shortExplanation: "Offers vague future promises to delay accountability.",
    description: "Deflects present responsibility with commitments that have no timeline or specificity. The promise itself becomes the resolution, even though nothing changes.",
    neutralizer: "Present Accountability Protocol",
  },
  "MANIPULATION DETECTED": {
    shortExplanation: "Uses covert influence to control the conversation.",
    description: "Employs indirect pressure tactics to shift the power dynamic. The manipulation may not be immediately obvious, but the cumulative effect is to limit the other person's agency.",
    neutralizer: "Grey Rock Response Pattern",
  },
  "NEUTRAL ACCOUNTABILITY": {
    shortExplanation: "Genuine self-reflection — this is healthy communication.",
    description: "The speaker acknowledges their role without deflecting, minimizing, or attaching conditions. This is accountability, not manipulation.",
    neutralizer: "No neutralization needed — this is healthy communication.",
  },
  "HEALTHY BOUNDARY": {
    shortExplanation: "No manipulation detected — constructive engagement.",
    description: "The speaker is setting a boundary or requesting space without coercion, guilt, or hidden conditions. This reflects healthy disengagement, not avoidance or control.",
    neutralizer: "No neutralization needed — this is healthy boundary-setting.",
  },
  "AMBIGUOUS RESPONSIBILITY": {
    shortExplanation: "Mixed signals — could be genuine disagreement or subtle pressure.",
    description: "Contains elements that could read as honest friction or as covert manipulation. Context, tone, and pattern of use determine whether this is insensitivity or intent.",
    neutralizer: "Clarification Protocol",
  },
  "SOFT INVALIDATION": {
    shortExplanation: "Subtly minimizes your perspective without overt aggression.",
    description: "Doesn't directly attack — but the effect is still dismissive. The other person's reaction is treated as slightly too much, slightly unreasonable, or slightly unnecessary.",
    neutralizer: "Gentle Boundary Protocol",
  },
  "NEUTRAL / LOW FRICTION": {
    shortExplanation: "No manipulation detected — genuine communication.",
    description: "This reflects normal conversational friction or healthy engagement. No coercive patterns identified.",
    neutralizer: "No action required.",
  },
  "AMBIGUOUS SIGNAL": {
    shortExplanation: "Ambiguous phrasing — may be poor communication rather than manipulation.",
    description: "The language contains signals that could reflect stress, insensitivity, or differing perspectives. Without a broader pattern, it's unclear whether this is deliberate.",
    neutralizer: "Clarification & Boundary Protocol",
  },
  "LOW RISK / NEUTRAL": {
    shortExplanation: "No manipulation detected — constructive engagement.",
    description: "This reflects genuine communication, accountability, or constructive engagement. No coercive patterns identified.",
    neutralizer: "No action required.",
  },
  "DIVERGENT REALITY": {
    shortExplanation: "Presents a different version of events — may be genuine.",
    description: "This disputes what happened without attacking the other person's credibility. It may reflect honest memory differences rather than intentional distortion.",
    neutralizer: "Clarification Protocol",
  },
  "CONSTRUCTIVE FEEDBACK": {
    shortExplanation: "Criticism that appears to come from constructive intent.",
    description: "While potentially uncomfortable, this feedback addresses behavior rather than identity and doesn't appear designed to control or diminish.",
    neutralizer: "No neutralization needed — consider the feedback on its merits.",
  },
  "MODERATE FRICTION / AMBIGUOUS": {
    shortExplanation: "Could be friction or subtle pressure — context determines intent.",
    description: "Sits on the line between insensitivity and manipulation. The same phrase could be thoughtless in one context and coercive in another.",
    neutralizer: "Awareness Protocol",
  },
};

// Map API type keys to display labels
export const PATTERN_LABELS: Record<string, string> = {
  healthy_boundary: "Healthy Boundary",
  neutral_accountability: "Neutral Accountability",
  ambiguous_responsibility: "Ambiguous Responsibility",
  soft_invalidation: "Soft Invalidation",
  guilt_induction: "Guilt-Tripping",
  shame_attack: "Shame Attack",
  gaslighting: "Gaslighting",
  urgency_pressure: "Urgency Pressure",
  authority_dominance: "Control Framing",
  victim_reversal: "Victim Reversal",
  passive_aggression: "Passive Aggression",
  blame_shift: "Blame Shifting",
  emotional_blackmail: "Emotional Blackmail",
  social_pressure: "Social Pressure",
  triangulation: "Triangulation",
  deflection: "Deflection",
  control_framing: "Control Framing",
  minimization: "Minimization",
  false_consensus: "False Consensus",
  moral_superiority: "Moral Superiority",
  tone_policing: "Tone Policing",
  future_proposing: "Future Proposing",
  invalidation: "Invalidation",
};
